import axios from "axios";
import { store } from "Store/mainStore";

class InstalledBaseService {

  /**
   * Get user state from Redux store
   */
  getState() {
    const reduxtStore = store.getState();
    const email = reduxtStore.authSlice?.login?.mail || "";
    const userId = reduxtStore.authSlice?.login?.id || "";
    return { userId, email };
  }

  /**
   * Fetch Installed Base Data with correct payload structure
   * sell_to_party and end_customer are passed as HEADERS
   */
  fetchInstalledBaseData = async (params: {
    type: string;
    network_ids: any;
    properties: string[];
    filter: string;
    sell_to_party: string;
    end_customer: string;
    limit?: number;
    offset?: number;
    order_by?: string;
  }) => {
    try {
      const {
        type,
        network_ids,
        properties,
        filter,
        sell_to_party,
        end_customer,
        limit = 100,
        offset = 0,
        order_by = "",
      } = params;

      const payload = {
        type,
        network_ids,
        properties,
        filter,
        limit,
        offset,
        order_by
      };

      const apiConfig = {
        method: 'post',
        url: `/v1/reconciliation/assets`,
        data: payload,
        customHeaders: {
          'sell-to-party': sell_to_party,
          'end-customer': end_customer,
        }
      };

      const response = await this.genericDatamartAPI(apiConfig);
      return response;
    } catch (error: any) {
      console.error("Error fetching installed base data:", error);
      return {
        status: false,
        message: error.message || 'Unknown error',
        data: [],
        summary: {
          total: 0,
          matched: 0,
          mismatch: 0,
          partially_matched: 0
        }
      };
    }
  }

  /**
   * Fetch Asset Details by Product ID
   * API: /v1/reconciliation/assets/details
   */
  fetchAssetDetails = async (params: {
    type: string;
    network_ids: string[];
    properties: string[];
    filter: string;
    sell_to_party: string;
    end_customer: string;
    limit?: number;
    offset?: number;
    order_by?: string;
    category?: boolean;
  }) => {
    try {
      const {
        type,
        network_ids,
        properties,
        filter,
        sell_to_party,
        end_customer,
        limit = 100,
        offset = 0,
        order_by = "",
        category = false
      } = params;

      const payload = category ? { type,
        network_ids,
        properties,
        filter,
        order_by,
        group_by: "product_family_id"
      } :{
        type,
        network_ids,
        properties,
        filter,
        limit,
        offset,
        order_by
      };
      const apiConfig = {
        method: 'post',
        url: `/v1/reconciliation/assets/details`,
        data: payload,
        // baseUrl: "http://localhost:4000",
        customHeaders: {
          'sell-to-party': sell_to_party,
          'end-customer': end_customer,
          ...(category && {
            'operation': 'categories'
          })
        }
      };

      const response = await this.genericDatamartAPI(apiConfig);
      return response;
    } catch (error: any) {
      console.error("Error fetching asset details:", error);
      return {
        status: false,
        message: error.message || 'Unknown error',
        data: []
      };
    }
  }

  /**
   * Export Installed Base Data as a file (CSV/Excel)
   */
  exportInstalledBaseData = async (params: {
    type: string;
    network_ids: any;
    properties: string[];
    filter: string;
    sell_to_party: string;
    end_customer: string;
    order_by?: string;
  }) => {
    try {
      const {
        type,
        network_ids,
        properties,
        filter,
        sell_to_party,
        end_customer,
        order_by = "",
      } = params;

      const { userId } = this.getState();

      const payload = {
        type,
        network_ids,
        properties,
        filter,
        order_by
      };

      const config: any = {
        method: 'post',
        url: `${process.env.REACT_APP_DATAMART}/v1/reconciliation/assets`,
        data: payload,
        responseType: 'blob', // Important for file downloads
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'sell-to-party': sell_to_party,
          'end-customer': end_customer,
          'operation': 'export-all'
        }
      };

      const response = await axios(config);
      return {
        status: true,
        data: response.data,
        headers: response.headers,
        statusCode: response.status
      };
    } catch (error: any) {
      console.error("Error exporting installed base data:", error);
      return {
        status: false,
        message: error.message || 'Export failed',
        statusCode: error?.response?.status
      };
    }
  }

  /**
   * A generic function to make API calls (GET, POST, PUT, DELETE) to the Datamart service.
   */
  async genericDatamartAPI({
    method,
    url,
    data,
    customHeaders = {},
    baseUrl = process.env.REACT_APP_DATAMART
  }: {
    method: string;
    url: string;
    data?: any;
    customHeaders?: Record<string, string>;
    baseUrl?: string;
  }) {
    try {
      const { userId } = this.getState();

      const config: any = {
        method: method,
        url: `${baseUrl}${url}`,
        data: data,
        headers: {
          'Content-Type': 'application/json',
          "x-user-id": userId,
          ...customHeaders
        }
      };

      const res = await axios(config);

      const response = {
        statusCode: res.status,
        statusText: res.statusText,
        status: res.data?.status,
        message: res.data?.message || '',
        data: res?.data?.data || [],
        summary: res?.data?.summary || {},
        total_count: res?.data?.totalCount || 0,  // Map to total_count for consistency
        totalCount: res?.data?.totalCount || 0,    // Keep both for compatibility
        pageCount: res?.data?.pageCount || 0,
        requestId: res?.data?.requestId || '',
      };

      return response;
    } catch (err: any) {
      console.error("Error in InstalledBase genericDatamartAPI:", err);
      const errMesg = err.response?.data?.errorMessage
        ? err.response?.data?.errorMessage
        : err?.message;

      return {
        data: [],
        status: err?.response?.data?.status || false,
        message: errMesg,
        statusCode: err?.response?.status
      };
    }
  }
}

export const installedBaseService = new InstalledBaseService();
export default installedBaseService;